import { useEffect, useState } from "react";
import { Occurrence, OccurrenceDispatch } from "../models/occurrence";
import useDealership from "./useDealership";
import { Dealership } from "../models/dealership";
import { fetchDealershipsByGeolocation, fetchDealerships, fetchDealershipsByStateAndCity } from "../api/fetchDealerships";
import { getAddressByGeoLocation } from "../services/assetService";
import { createv2Route, getRoute, Route } from "../api/routing";
import { useDrivers } from "./useDrivers";
import { AssetGeoLocation } from "../models/asset";
import { fetchAssetGeoLocation } from "../api/fetchAssets";
import { useIntl } from "react-intl";

export const reasonsRefusalOptions = [
    { id: "NO_RESOURCE", label: "Não possui recursos" },
    { id: "NO_PARTS", label: "Não possui peças" },
    { id: "NO_STAFF", label: "Não possui técnico" },
    { id: "NA", label: "Outro" },
];

export const optionsOccurrenceType = [
    { id: "ASSISTANCE", label: "Socorro Mecânico" },
    { id: "WINCH", label: "Guincho" },
];

export const optionsPayer = [
    { id: "VOKS", label: "Volks|Total" },
    { id: "CHAME_VOLKS", label: "ChameVolks/MAN Service" },
    { id: "UNIFIED_REBOQUE", label: "Prestador Reboque Unificado" },
    { id: "SG", label: "SG (Sistema de Garantia)" },
    { id: "PENDING_ANALYSIS", label: "Pendente Análise Garantia" },
    { id: "OUT_OF_WARRANTY", label: "Fora de Garantia" },
    { id: "VOLKS_CONFIA", label: "VolksConfia" },
    { id: "EXPORTATION", label: "Exportação" },
    { id: "OTHER", label: "Outro" },
];

/**
 * Dipacth card
 */
interface DispatchProps {
    occurrence?: Occurrence | null;
    occurrenceDispatch: OccurrenceDispatch;
    cancelDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch, reason: string, description: string) => Promise<{ wasCanceled: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
    removeDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch) => Promise<void>;
    status?: string;
    persona?: 'MANAGER' | 'TOWER' | 'CONSULTANT' | 'PARTNERSHIP';
}
export function useDispatch(props: DispatchProps) {
    const { getDealershipByDN } = useDealership();
    const [dealership, setDealership] = useState<Dealership | null>(null);

    const { drivers } = useDrivers();

    const [route, setRoute] = useState<Route | null>(null);
    const [showCancelReasonDialog, setShowCancelReasonDialog] = useState<boolean>(false);

    useEffect(() => {
        if (props.occurrence?.dealershipDn) {
            getDealershipByDN(props.occurrenceDispatch.dn ?? props.occurrence.dealershipDn).then((dealership) => {
                if (dealership) {
                    setDealership(dealership);
                }
            });
        }
    }, [props.occurrence?.dealershipDn, props.occurrenceDispatch.dn]);

    useEffect(() => {
        if (props.occurrenceDispatch.route) {
            getRoute(props.occurrenceDispatch.route).then(({ route, error }) => {
                if (route) {
                    setRoute(route);
                } else {
                    setRoute(null);
                }
            });
        }
    }, [props.occurrenceDispatch.route]);

    const [showCancelDialog, setShowCancelDialog] = useState(false);

    const statusColor = (status: string) => {
        switch (status) {
            case "Indisponível para atendimento":
                return "#AB2A34";
            case "Aguardando concessionária":
                return "#428AC9";
            default:
                return "#10B981";
        }
    }

    const [newStatus, setNewStatus] = useState<string>(props.status ?? "");
    const [newRefusal, setNewRefusal] = useState<{ reason: string, description: string } | null>({ reason: props.occurrenceDispatch.reasonRefusal ?? "", description: props.occurrenceDispatch.descriptionRefusal ?? "" });
    const [newDriverName, setNewDriverName] = useState<string | null>(props.occurrenceDispatch.driverName ?? null);

    useEffect(() => {
        setNewStatus(props.status ?? "");
    }, [props.status]);

    return {
        optionsOccurrenceType,
        dealership,
        route,
        statusColor,
        newStatus,
        setNewStatus,
        showCancelDialog,
        setShowCancelDialog,
        showCancelReasonDialog,
        setShowCancelReasonDialog,
        newRefusal,
        setNewRefusal,
        drivers,
        newDriverName,
        setNewDriverName,
        optionsPayer,
    }
}

/**
 * Dispatch form
 */
interface DispatchFormProps {
    occurrence?: Occurrence | null;
    upsertDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch) => Promise<{ wasCreated: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
    dispatches: OccurrenceDispatch[];
    updateDispatchList: (dispatch: OccurrenceDispatch) => void;
    persona?: 'MANAGER' | 'TOWER' | 'CONSULTANT' | 'PARTNERSHIP';
}

export function useDispatchForm(props: DispatchFormProps) {
    const intl = useIntl();
    const optionsReleasePayment = [
        { id: "true", label: "Sim" },
        { id: "false", label: "Não" },
    ];

    const [loading, setLoading] = useState(false);
    const [upsertDispatchLoading, setAddDispatchLoading] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorDialogBody, setErrorDialogBody] = useState<string>("");
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const [showChooseDealershipDialog, setShowChooseDealershipDialog] = useState(false);

    const [dispatchForm, setDispatchForm] = useState<{
        id?: string;
        occurrenceType?: string;
        payer?: string;
        releasePayment?: string;
    }>({
        id: undefined,
        occurrenceType: undefined,
        payer: undefined,
        releasePayment: undefined,
    });

    const handleAddDispatch = async (occurrenceDn: string): Promise<boolean> => {
        if (!props.occurrence || !props.upsertDispatch) {
            setShowErrorDialog(true);
            setErrorDialogBody(intl.formatMessage({ id: 'intl-msg:dispatch.error.occurrence-not-found' }));
            return false;
        }
        
        setAddDispatchLoading(true);
        const { error, dispatch } = await props.upsertDispatch(props.occurrence, {
            occurrenceType: mapOccurrenceDispatchType(dispatchForm.occurrenceType),
            payer: dispatchForm.payer,
            authorizePayment: dispatchForm.releasePayment === "true",
            dn: occurrenceDn,
            route: '-',
        });

        if (error) {
            setShowErrorDialog(true);
            setErrorDialogBody(error ?? intl.formatMessage({ id: 'intl-msg:dispatch.error.body' }));
            setAddDispatchLoading(false);
            return false;
        }

        setDispatchForm({
            id: dispatch?.id,
            occurrenceType: dispatch?.occurrenceType,
            payer: dispatch?.payer,
            releasePayment: dispatch?.authorizePayment ? "true" : "false",
        });
        setAddDispatchLoading(false);
        return true;
    }
    
    const handleUpdateDispatch = async (dn: string, route: string, originLocation: string, destinationLocation: string) => {
        if (!props.occurrence || !props.upsertDispatch) {
            setShowErrorDialog(true);
            setErrorDialogBody(intl.formatMessage({ id: 'intl-msg:dispatch.error.occurrence-not-found' }));
            return;
        }

        setLoading(true);
        const { error, dispatch } = await props.upsertDispatch(props.occurrence, {
            id: dispatchForm.id,
            occurrenceType: mapOccurrenceDispatchType(dispatchForm.occurrenceType),
            payer: dispatchForm.payer,
            authorizePayment: dispatchForm.releasePayment === "true",
            dn,
            route,
            originLocation: originLocation,
            destinationLocation: destinationLocation,
        });
        if (dispatch) {
            setShowSuccessDialog(true);
            props.updateDispatchList(dispatch);
        } else {
            setShowErrorDialog(true);
            setErrorDialogBody(error ?? intl.formatMessage({ id: 'intl-msg:dispatch.error.body' }));
        }
        setLoading(false);
    }

    const mapOccurrenceDispatchType = (occurrenceType?: string) => {
        switch (occurrenceType) {
            case "ASSISTANCE":
                return "ASSISTANCE";
            case "Socorro Mecânico":
                return "ASSISTANCE";
            case "Guincho":
                return "WINCH";
            case "WINCH":
                return "WINCH";
            default:
                return occurrenceType;
        }
    }

    return {
        optionsOccurrenceType,
        optionsPayer,
        optionsReleasePayment,
        loading,
        upsertDispatchLoading,
        showErrorDialog,
        errorDialogBody,
        showSuccessDialog,
        setShowErrorDialog,
        setShowSuccessDialog,
        dispatchForm,
        setDispatchForm,
        handleAddDispatch,
        handleUpdateDispatch,
        showChooseDealershipDialog,
        setShowChooseDealershipDialog,
    };
}

/**
 * Dispatch choose dealership
 */
interface AddressForm {
    address: string;
    neighborhood: string;
    city: string;
    state: string;
    label?: string;
    geoLocation?: AssetGeoLocation;
    
}

interface Props { 
    show: boolean, 
    responsibleId: string, 
    assetId: string, 
    occurrenceType?: string, 
    chassis?: string,
    ocurrenceId?: string, 
    dispatchId?: string,
    vehicleName?: string 
}

export function useDispatchChooseDealership(props: Props) {
    const [loading, setLoading] = useState<boolean>(false);
    const [saveLoading, setSaveLoading] = useState<boolean>(false);
    const [addressForm, setAddressForm] = useState<AddressForm>({ address: "", neighborhood: "", city: "", state: "", label: "", geoLocation: undefined });
    const [addressGeoLocation, setAddressGeoLocation] = useState<{ lat: number, lng: number }>({ lat: -23.550573750495325, lng: -46.64411772565403 });
    const [showSearchDealership, setShowSearchDealership] = useState<boolean>(false);
    const [searchDealership, setSearchDealership] = useState<boolean>(false);
    const [searchDealershipTerm, setSearchDealershipTerm] = useState("");
    const [dealerships, setDealerships] = useState<Dealership[]>([]);

    const [dealershipSelected, setDealershipSelected] = useState<{ dn: string, address: string } | null>(null);
    const [showSearchConcessionaria, setShowSearchConcessionaria] = useState<boolean>(false);

    const [createRouteError, setCreateRouteError] = useState<string | null>(null);

    const [enableLocation, setEnableLocation] = useState(false);
    const [assetGeoLocation, setAssetGeoLocation] = useState<AssetGeoLocation>();
    const [initialAssetAddress, setInitialAssetAddress] = useState<{ address: string, neighborhood: string, state: string, city: string, geoLocation: AssetGeoLocation }>();

    useEffect(() => {
        setAddressForm({ address: "", neighborhood: "", city: "", state: "" });
    }, [props.show]);

    useEffect(() => {
        const fetchData = async () => {
            const assetGeoLocation = await fetchAssetGeoLocation(props.assetId);
            if (assetGeoLocation?.lat && assetGeoLocation?.lng) {
                setAssetGeoLocation(assetGeoLocation);
                
                const assetAddress = await getAddressByGeoLocation(assetGeoLocation?.lat, assetGeoLocation?.lng);
                setInitialAssetAddress({ 
                    address: assetAddress.address ?? '', 
                    neighborhood: assetAddress.neighborhood ?? '', 
                    state: assetAddress.state ?? '', 
                    city: assetAddress.city ?? '', 
                    geoLocation: assetGeoLocation
                });

                updateAddress();
            } else {
                setEnableLocation(true);
            }
        }

        fetchData();


    }, []);

    const searchDealerships = async (geoLocation: { lat: number, lng: number }) => {
        setLoading(true);
        setAddressGeoLocation(geoLocation);
        const dealerships = await fetchDealershipsByGeolocation(geoLocation.lat, geoLocation.lng);
        setLoading(false);
        setDealerships(dealerships);
        setSearchDealership(true);
    }

    const updateAddress = async (geolocation?: AssetGeoLocation) => {
        const address = await getAddressByGeoLocation(geolocation?.lat, geolocation?.lng);
        
        setAddressForm({
            address: address.address,
            neighborhood: address.neighborhood,
            city: address.city,
            state: address.state,
        });

        if (geolocation) await searchDealerships(geolocation);
        else await fetchDealershipsByStateAndCity(addressForm.state, addressForm.city);
    }

    const handleSetEnableLocation = (manualLocationEnabled: boolean) => {
        setEnableLocation(manualLocationEnabled);


        if (manualLocationEnabled) [setAssetGeoLocation({ lat: -23.550573750495325, lng: -46.64411772565403 }), setAddressForm({ address: '', neighborhood: '', state: '', city: '' })]
        else [
            setAssetGeoLocation(initialAssetAddress?.geoLocation), 
            setAddressForm({ address: initialAssetAddress?.address ?? '', neighborhood: initialAssetAddress?.neighborhood ?? '', state: initialAssetAddress?.state ?? '', city: initialAssetAddress?.city ?? '' })
        ];
        setShowSearchConcessionaria(false);
    };
   
    const createRoutingRoute = async (): Promise<{ routeId: string, origin?: string, destination?: string }> => {
        if (!dealershipSelected) {
            setCreateRouteError("Concessionária não selecionada");
            return { routeId: "", origin: "", destination: "" };
        }

        const firstAddress = dealershipSelected.address;
        const secondAddress = addressForm.label ?? `${[addressForm.address, addressForm.neighborhood, addressForm.city, addressForm.state].filter(Boolean).join(", ")}`;

        // If the occurrence type is winch, the origin is the vehicle address and the destination is the dealership address
        const origin = props.occurrenceType === 'WINCH' ? secondAddress : firstAddress;
        const destination = props.occurrenceType === 'WINCH' ? firstAddress : secondAddress;

        // If the occurrence type is winch, the vehicle type is TRUCK else is CAR
        const vehicleType = props.occurrenceType === 'WINCH' ? "TRUCK" : "CAR";

        // If the occurrence type is winch, the stay time is 01:00 hour else is 00:00
        const stayTime = props.occurrenceType === 'WINCH' ? "01:00" : "00:00";
        
        const { wasCreated, routeId, error } = await createv2Route(
                origin, 
                destination, 
                `Acionamento ${new Date().toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, 
                vehicleType,
                dealershipSelected.dn,
                props.chassis,
                props.dispatchId,
                props.vehicleName,
                stayTime
            );
        if (wasCreated) {
            setCreateRouteError(null);
            setDealerships([]);
            return { routeId, origin, destination };
        } else {
            setCreateRouteError(error ?? "Erro ao criar rota");
            return { routeId: "", origin: "", destination: "" };
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            const dealerships = await fetchDealerships(searchDealershipTerm);
            setDealerships(dealerships);
        }

        if(searchDealershipTerm?.length > 2) fetchData();
    }, [searchDealershipTerm]);

    return {
        loading,
        saveLoading,
        setSaveLoading,
        addressForm,
        setAddressForm,
        addressGeoLocation,
        searchDealership,
        searchDealershipTerm,
        setSearchDealershipTerm,
        dealerships,
        setDealershipSelected,
        dealershipSelected,
        searchDealerships,
        createRouteError,
        setCreateRouteError,
        createRoutingRoute,
        showSearchDealership,
        showSearchConcessionaria, setShowSearchConcessionaria,
        enableLocation, handleSetEnableLocation,
        assetGeoLocation,
        setAssetGeoLocation,
    }
}